"use client";

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { CheckCircle2, XCircle, Info } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Canonical operator-feedback toast.
 *
 * Consolidates three previously-bespoke implementations (Issues page,
 * Inbox page, Follow-ups page) so success/error feedback for any
 * action looks identical regardless of which page raised it. Mounted
 * once via `<ToastProvider>` inside `<AppShell>`; pages call
 * `useToast()` and push entries.
 *
 *   const toast = useToast();
 *   toast.ok("Issue ABC-123 approved");
 *   toast.err("Failed to approve — retry?");
 *   toast.info("Patch queued for delivery");
 *
 *   // For action-result patterns, the convenience helper accepts
 *   // a (kind, text) tuple so legacy callsites can swap one-for-one
 *   // without restructuring:
 *   toast.show("ok", "ABC-123 approved");
 *
 * Auto-dismiss after `defaultDurationMs` (3.2s — same as Issues had);
 * pages can override per call. Manual dismiss via the X button. The
 * toast stack lives bottom-right on desktop, anchored to the same
 * z-index as Modal-fallback overlays so toasts always sit above
 * row content but below an open ConfirmModal.
 */

export type ToastKind = "ok" | "err" | "info";

export type ToastUndo = {
  /** Button label, defaults to "Undo". */
  label?: string;
  /** Handler — fires on click. The toast dismisses immediately. */
  run: () => void | Promise<void>;
};

export type ToastOptions = {
  /** Optional secondary line — typically the entity that triggered. */
  detail?: string;
  /**
   * When set, the toast surfaces an inline "Undo" button. Lets
   * pages skip a ConfirmModal for reversible actions (Reject an
   * issue / Ignore an email / Discard a draft) — operator can
   * undo within the toast lifetime instead of being interrupted
   * by a modal up front.
   */
  undo?: ToastUndo;
  /** Override the auto-dismiss duration in ms. Toasts with `undo`
   *  default to 6s instead of 3.2s so operators have a real chance
   *  to click Undo before it disappears. */
  durationMs?: number;
};

type ToastEntry = {
  id: number;
  kind: ToastKind;
  text: string;
  detail?: string;
  undo?: ToastUndo;
};

type ToastApi = {
  ok: (text: string, opts?: ToastOptions | string) => void;
  err: (text: string, opts?: ToastOptions | string) => void;
  info: (text: string, opts?: ToastOptions | string) => void;
  /** Tuple form for one-for-one migrations of the legacy
   *  `(kind, text)` callsites. */
  show: (kind: ToastKind, text: string, opts?: ToastOptions | string) => void;
  /** Dismiss everything — useful when the operator navigates away
   *  mid-async (route change clears stale queued toasts). */
  clear: () => void;
};

const ToastCtx = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastCtx);
  if (!api) throw new Error("useToast must be called inside <ToastProvider>");
  return api;
}

const DEFAULT_DURATION_MS = 3200;

export function ToastProvider({
  children,
  defaultDurationMs = DEFAULT_DURATION_MS,
}: {
  children: React.ReactNode;
  defaultDurationMs?: number;
}) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);

  // Accept the legacy `detail: string` shorthand so existing
  // callsites don't have to switch to the options object form.
  // `undefined` and `string` map to the no-options path; the
  // options object form unlocks Undo + per-toast duration.
  const show = useCallback(
    (kind: ToastKind, text: string, opts?: ToastOptions | string) => {
      const o: ToastOptions =
        typeof opts === "string" ? { detail: opts } : (opts ?? {});
      const id = ++idRef.current;
      setToasts((t) => [...t, { id, kind, text, detail: o.detail, undo: o.undo }]);
      // Toasts with Undo get more breathing room so the operator
      // has a real chance to click before the auto-dismiss.
      const duration = o.durationMs ?? (o.undo ? 6000 : defaultDurationMs);
      setTimeout(() => {
        setToasts((t) => t.filter((x) => x.id !== id));
      }, duration);
    },
    [defaultDurationMs],
  );

  const api = useMemo<ToastApi>(() => ({
    ok:    (text, opts) => show("ok",   text, opts),
    err:   (text, opts) => show("err",  text, opts),
    info:  (text, opts) => show("info", text, opts),
    show,
    clear: () => setToasts([]),
  }), [show]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={(id) => setToasts((t) => t.filter((x) => x.id !== id))} />
    </ToastCtx.Provider>
  );
}

function ToastViewport({
  toasts, onDismiss,
}: {
  toasts: ToastEntry[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div
      role="region"
      aria-label="Notifications"
      className="fixed bottom-5 right-5 z-40 flex flex-col gap-2 pointer-events-none max-w-[380px]"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastEntry; onDismiss: () => void }) {
  const Icon = toast.kind === "ok" ? CheckCircle2 : toast.kind === "err" ? XCircle : Info;
  const iconCls =
    toast.kind === "ok"  ? "text-status-low"      :
    toast.kind === "err" ? "text-status-critical" :
                           "text-status-info";
  const borderCls =
    toast.kind === "ok"  ? "border-status-low/30"      :
    toast.kind === "err" ? "border-status-critical/40" :
                           "border-status-info/30";
  // Toasts WITH Undo render as a non-button shell so the click on
  // the Undo affordance doesn't double-dismiss + double-trigger.
  // Toasts WITHOUT Undo stay as a single click-to-dismiss button —
  // operators can always click the toast itself to dismiss.
  if (toast.undo) {
    return (
      <div
        className={cn(
          "pointer-events-auto card px-3 py-2 text-[12.5px] flex items-start gap-2 shadow-lg w-full",
          "animate-in fade-in slide-in-from-bottom-2",
          borderCls,
        )}
      >
        <Icon size={14} className={cn("shrink-0 mt-0.5", iconCls)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-fg">{toast.text}</div>
          {toast.detail && <div className="text-[11.5px] text-fg-muted mt-0.5">{toast.detail}</div>}
        </div>
        <button
          type="button"
          onClick={() => {
            void toast.undo!.run();
            onDismiss();
          }}
          className="shrink-0 ml-1 text-[11.5px] font-medium text-brand-300 hover:text-brand-200 focus-ring rounded px-1.5 py-0.5"
        >
          {toast.undo.label ?? "Undo"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 text-fg-muted hover:text-fg focus-ring rounded px-1"
        >
          ✕
        </button>
      </div>
    );
  }
  // Auto-dismiss is owned by the provider; click also dismisses so an
  // operator who's seen the message can clear it before the timer.
  return (
    <button
      type="button"
      onClick={onDismiss}
      className={cn(
        "pointer-events-auto card px-3 py-2 text-[12.5px] flex items-start gap-2 shadow-lg w-full text-left",
        "animate-in fade-in slide-in-from-bottom-2",
        borderCls,
      )}
    >
      <Icon size={14} className={cn("shrink-0 mt-0.5", iconCls)} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-fg">{toast.text}</div>
        {toast.detail && <div className="text-[11.5px] text-fg-muted mt-0.5">{toast.detail}</div>}
      </div>
    </button>
  );
}

/**
 * Side-effect-free no-op API used inside test environments and any
 * surface (e.g. SSR previews) that mounts a component using `useToast`
 * without a real provider. The provider is gated by AppShell, so
 * pre-auth surfaces like /login and /docs would otherwise crash.
 *
 * Pages should NEVER reach for this directly — calling `useToast()`
 * from inside `<ToastProvider>` is the canonical path. The fallback
 * exists purely so reusable components can be rendered in isolation
 * (Storybook, smoke tests).
 */
export function createNoopToastApi(): ToastApi {
  const noop = (..._args: unknown[]) => { /* no-op */ };
  return {
    ok:    noop as ToastApi["ok"],
    err:   noop as ToastApi["err"],
    info:  noop as ToastApi["info"],
    show:  noop as ToastApi["show"],
    clear: noop as ToastApi["clear"],
  };
}
